import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Banknote, ChevronRight, Percent, Receipt, SearchX } from "lucide-react";
import { z } from "zod";
import {
  PAYMENT_SORT_FIELDS,
  SORT_DIRECTIONS,
  auDate,
  fromYmd,
  isValidYmd,
  money,
  type PaymentRow,
  type PaymentSortField,
} from "@contractorhub/shared";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  StatCard,
  type Column,
} from "../../../components/ui";
import { Amount, AmountOrZero, TaxIncluded, audOnly, noCurrencyHint } from "../../../components/invoices/invoice-display";
import { adminContractorsQuery, adminPaymentsQuery } from "../../../lib/queries";
import { RangePresets } from "../../../components/range-presets";

// A reversed range (an old or hand-typed link) is swapped rather than sent:
// the API answers it with a 400, and a 400 in the loader is the error page.
const searchSchema = z
  .object({
    q: z.string().max(120).optional(),
    contractorId: z.string().optional(),
    kind: z.enum(["CONTRACTOR", "EXPENSE"]).optional(),
    from: z.string().refine(isValidYmd).optional(),
    to: z.string().refine(isValidYmd).optional(),
    sort: z.enum(PAYMENT_SORT_FIELDS).default("paidAt"),
    dir: z.enum(SORT_DIRECTIONS).default("desc"),
    page: z.coerce.number().int().min(1).default(1),
  })
  .transform((search) =>
    search.from && search.to && search.from > search.to
      ? { ...search, from: search.to, to: search.from }
      : search
  );

export const Route = createFileRoute("/_authed/_admin/payments")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminPaymentsQuery(deps)),
      context.queryClient.ensureQueryData(adminContractorsQuery),
    ]),
  component: PaymentsPage,
});

/**
 * The payment history (requirement 18): every paid contractor invoice, by the
 * day it was paid, with the reference and who recorded it. Read-only — a
 * payment is recorded on the invoice (Mark as paid), and this is where they are
 * all seen together. Totals are over every filtered payment, not the page.
 */
function PaymentsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(adminPaymentsQuery(search));
  const { data: contractorData } = useSuspenseQuery(adminContractorsQuery);

  // Merge into the search and reset the page, as the inbox does.
  const patch = (next: Partial<typeof search>) =>
    void navigate({ to: "/payments", search: { ...search, ...next, page: 1 } });
  const open = (row: PaymentRow) =>
    row.kind === "EXPENSE"
      ? void navigate({ to: "/expenses/$id", params: { id: row.invoiceId } })
      : void navigate({ to: "/invoices/$id", params: { id: row.invoiceId } });
  const filtered = Boolean(search.q || search.contractorId || search.kind || search.from || search.to);

  const columns: Column<PaymentRow>[] = [
    {
      key: "paidAt",
      header: "Paid",
      sortable: true,
      cell: (row) => (
        <span className="whitespace-nowrap tabular-nums text-text">
          {auDate(fromYmd(row.paidAt))}
        </span>
      ),
    },
    {
      key: "contractor",
      header: "Paid to",
      sortable: true,
      cell: (row) => (
        <div className="min-w-0">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              open(row);
            }}
            className="block max-w-full truncate rounded-sm text-left font-medium text-text hover:text-accent"
          >
            {row.contractorName}
          </button>
          <div className="truncate text-xs text-subtle">
            {row.kind === "EXPENSE" ? `Expense${row.contractorCompany ? ` · ${row.contractorCompany}` : ""}` : row.contractorCompany}
          </div>
        </div>
      ),
    },
    {
      key: "invoice",
      header: "Invoice",
      cell: (row) => (
        <div className="whitespace-nowrap">
          <div className="text-text">{row.invoiceNumber ?? <span className="text-subtle">No number</span>}</div>
          <div className="text-xs text-subtle">{row.periodLabel}</div>
        </div>
      ),
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      sortable: true,
      cell: (row) => (
        <div className="whitespace-nowrap">
          <Amount value={row.total} currency={row.currency} className="font-semibold text-text" />
          {/* A part payment (CINV-23) says what it was part of. */}
          {row.kind === "CONTRACTOR" && row.invoiceTotal !== null && row.total !== row.invoiceTotal && (
            <div className="text-xs text-subtle">
              part of {money(row.invoiceTotal, row.currency)}
            </div>
          )}
          <TaxIncluded taxAmount={row.taxAmount} total={row.total} currency={row.currency} />
        </div>
      ),
    },
    {
      key: "reference",
      header: "Reference",
      priority: "md",
      cell: (row) =>
        row.paymentReference ?? <span className="text-subtle">—</span>,
    },
    {
      key: "recorded",
      header: "Recorded by",
      priority: "lg",
      cell: (row) => (
        <div className="whitespace-nowrap text-sm">
          <div className="text-text">{row.payerName ?? "—"}</div>
          {row.approverName && (
            <div className="text-xs text-subtle">approved by {row.approverName}</div>
          )}
        </div>
      ),
    },
    {
      key: "open",
      header: <span className="sr-only">Open</span>,
      align: "right",
      width: "w-10",
      cell: (row) => (
        <button
          type="button"
          aria-label={`Open ${row.contractorName}, ${row.periodLabel}`}
          onClick={(event) => {
            event.stopPropagation();
            open(row);
          }}
          className="grid h-8 w-8 place-items-center rounded-md text-subtle transition-colors hover:bg-surface-hover hover:text-text"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Payments"
        description="Everything paid out — contractor invoices and expenses — by the day it was paid."
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Payments" value={data.summary.count} icon={Receipt} />
        <StatCard
          label="Paid"
          value={<AmountOrZero value={data.summary.total} />}
          hint={noCurrencyHint(data.summary.noCurrency) ?? undefined}
          icon={Banknote}
          tone="success"
        />
        <StatCard
          label="GST paid"
          value={<AmountOrZero value={audOnly(data.summary.tax)} />}
          icon={Percent}
          hint="Input tax on the AUD invoices paid in this view"
        />
      </div>

      <Card className="mb-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Field label="Search" className="sm:col-span-2">
            <SearchInput
              value={search.q ?? ""}
              placeholder="Name, invoice # or reference"
              onChange={(q) => patch({ q: q || undefined })}
            />
          </Field>
          <Field label="Contractor">
            <Select
              value={search.contractorId ?? ""}
              onChange={(e) => patch({ contractorId: e.target.value || undefined })}
            >
              <option value="">All contractors</option>
              {contractorData.contractors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Kind">
            <Select
              value={search.kind ?? ""}
              onChange={(e) => patch({ kind: (e.target.value || undefined) as typeof search.kind })}
            >
              <option value="">All payments</option>
              <option value="CONTRACTOR">Contractor invoices</option>
              <option value="EXPENSE">Expenses</option>
            </Select>
          </Field>
          <Field label="Paid from">
            <Input
              type="date"
              value={search.from ?? ""}
              onChange={(e) => patch({ from: e.target.value || undefined })}
            />
          </Field>
          <Field label="Paid to">
            <Input
              type="date"
              value={search.to ?? ""}
              onChange={(e) => patch({ to: e.target.value || undefined })}
            />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <RangePresets value={search} onChange={patch} />
          {filtered && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void navigate({ to: "/payments", search: { sort: search.sort, dir: search.dir, page: 1 } })
              }
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <DataTable
          caption="Payments"
          columns={columns}
          rows={data.rows}
          getRowKey={(row) => `${row.kind}:${row.paymentId ?? row.invoiceId}`}
          onRowClick={open}
          sort={{ key: search.sort, direction: search.dir }}
          onSortChange={(next) => {
            const sort = next.key as PaymentSortField;
            patch({ sort, dir: search.sort === sort && search.dir === "desc" ? "asc" : "desc" });
          }}
          empty={
            filtered ? (
              <EmptyState icon={SearchX} title="No payments match" description="Try a wider date range or clear the filters." />
            ) : (
              <EmptyState icon={Banknote} title="No payments yet" description="Invoices and expenses appear here once they are marked as paid." />
            )
          }
        />
        <Pagination
          page={data.pageInfo.page}
          pageSize={data.pageInfo.pageSize}
          total={data.pageInfo.total}
          onPageChange={(page) => void navigate({ to: "/payments", search: { ...search, page } })}
        />
      </Card>
    </div>
  );
}
